import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Edit2, FileText, Building2, Calendar, Package, DollarSign, Clock,
  CheckCircle, AlertCircle, XCircle, Download, Printer, Mail, Phone, Truck, Loader2
} from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import { toast } from 'react-hot-toast';
import { purchaseInvoicesAPI } from '../../services/part2-api';
import { getApiErrorMessage } from '../../utils/apiErrors';

const STATUS_CONFIG = {
  draft: { bg: 'bg-slate-100', text: 'text-slate-700', label: 'Draft', icon: Clock },
  unpaid: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Unpaid', icon: AlertCircle },
  partially_paid: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Partially Paid', icon: Clock },
  paid: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Paid', icon: CheckCircle },
  overdue: { bg: 'bg-red-100', text: 'text-red-700', label: 'Overdue', icon: AlertCircle },
  cancelled: { bg: 'bg-slate-100', text: 'text-slate-500', label: 'Cancelled', icon: XCircle }
};

const PurchaseInvoiceDetailPage = () => {
  const { businessId, id } = useParams();
  const navigate = useNavigate();
  const { activeBusiness } = useBusiness();
  const bid = businessId || activeBusiness?.id;

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    fetchInvoice();
  }, [id]);

  const fetchInvoice = async () => {
    try {
      const response = await purchaseInvoicesAPI.get(id);
      setInvoice(response.data);
    } catch (e) {
      toast.error('Invoice not found');
      navigate(`/business/${bid}/purchase-invoices`);
    }
    setLoading(false);
  };

  const handleBack = () => navigate(`/business/${bid}/purchase-invoices`);

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-[#F8F9FB]">
        <div className="text-center">
          <div className="w-10 h-10 border-[3px] border-slate-200 border-t-teal-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[14px] text-slate-500">Loading invoice details...</p>
        </div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="h-full flex items-center justify-center bg-[#F8F9FB]">
        <div className="text-center">
          <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-[16px] text-slate-600 font-medium">Purchase Invoice not found</p>
          <button onClick={handleBack} className="mt-4 text-teal-600 hover:underline text-[14px]">
            ← Back to Purchase Invoices
          </button>
        </div>
      </div>
    );
  }

  const statusConfig = STATUS_CONFIG[invoice.status] || STATUS_CONFIG.draft;
  const StatusIcon = statusConfig.icon;
  const items = invoice.items || [];

  return (
    <div className="h-full flex flex-col bg-[#F8F9FB] animate-pageFromRight">
      {/* PREMIUM HEADER */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Left: Back + Invoice Info */}
            <div className="flex items-center gap-4">
              <button
                onClick={handleBack}
                className="group flex items-center justify-center w-10 h-10 rounded-xl bg-slate-100 hover:bg-slate-200 transition-all duration-200 active:scale-95"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600 group-hover:text-slate-800 transition-colors" />
              </button>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-[20px] font-bold text-slate-800">{invoice.invoice_number}</h1>
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[12px] font-semibold ${statusConfig.bg} ${statusConfig.text}`}>
                    <StatusIcon className="w-3.5 h-3.5" />
                    {statusConfig.label}
                  </span>
                </div>
                <p className="text-[13px] text-slate-500 mt-0.5">
                  Vendor Bill: {invoice.supplier_invoice_no || '—'} • {items.length} item{items.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>

            {/* Right: Action Buttons */}
            <div className="flex items-center gap-2">
              {invoice.status === 'draft' && (
                <button
                  onClick={() => navigate(`/business/${bid}/purchase-invoices/${id}/edit`)}
                  className="inline-flex items-center gap-2 px-4 py-2.5 text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 text-[13px] font-medium active:scale-[0.98]"
                >
                  <Edit2 className="w-4 h-4" />
                  Edit
                </button>
              )}
              {['unpaid', 'partially_paid', 'overdue'].includes(invoice.status) && (
                <button
                  onClick={() => navigate(`/business/${bid}/payments-made/new?supplier_id=${invoice.supplier_id}&invoice_id=${invoice.id}`)}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-all duration-200 text-[13px] font-medium shadow-sm shadow-teal-600/20 active:scale-[0.98]"
                >
                  <DollarSign className="w-4 h-4" />
                  Record Payment
                </button>
              )}
              <div className="w-px h-8 bg-slate-200 mx-1"></div>
              <button onClick={async () => { try { const r = await purchaseInvoicesAPI.pdf(id); const u = window.URL.createObjectURL(r.data); const a = document.createElement('a'); a.href = u; a.download = `PI_${invoice?.invoice_number}.pdf`; a.click(); window.URL.revokeObjectURL(u); } catch(e) { toast.error(getApiErrorMessage(e, 'PDF download failed')); }}} className="inline-flex items-center justify-center w-10 h-10 text-slate-600 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 active:scale-[0.98]" title="Download PDF">
                <Download className="w-4 h-4" />
              </button>
              <button onClick={async () => { setSendingEmail(true); try { const r = await purchaseInvoicesAPI.sendEmail(id); toast.success(r.data.message || 'Email sent'); if(r.data.note) toast(r.data.note, {icon:'info'}); fetchInvoice(); } catch(e) { toast.error(getApiErrorMessage(e, 'Email failed')); } finally { setSendingEmail(false); }}} disabled={sendingEmail} className="inline-flex items-center justify-center w-10 h-10 text-slate-600 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed" title="Send Email">
                {sendingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* MAIN CONTENT - Premium 3-Column Layout */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        <div className="max-w-[1600px] mx-auto">
          <div className="grid grid-cols-12 gap-5">
            
            {/* LEFT COLUMN - Supplier & Invoice Info (3 cols) */}
            <div className="col-span-12 lg:col-span-3 space-y-4">
              
              {/* Supplier Card */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center shadow-sm">
                    <Building2 className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-[14px] font-semibold text-slate-800">Supplier</h2>
                </div>
                
                {invoice.supplier_name ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-full bg-teal-100 flex items-center justify-center">
                        <span className="text-[16px] font-bold text-teal-700">
                          {invoice.supplier_name?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="text-[14px] font-semibold text-slate-800">{invoice.supplier_name}</p>
                        <p className="text-[12px] text-slate-500">Supplier</p>
                      </div>
                    </div>
                    
                    {invoice.supplier_gstin && (
                      <div className="flex items-center gap-2 text-[13px] text-slate-600">
                        <Building2 className="w-4 h-4 text-slate-400" />
                        GSTIN: {invoice.supplier_gstin}
                      </div>
                    )}
                    
                    {invoice.supplier_address && (
                      <div className="text-[12px] text-slate-500 mt-2">
                        {invoice.supplier_address}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-[13px] text-slate-500">No supplier selected</p>
                  </div>
                )}
              </div>

              {/* Invoice Info Card */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-slate-500 to-slate-600 flex items-center justify-center shadow-sm">
                    <FileText className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-[14px] font-semibold text-slate-800">Invoice Info</h2>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-[12px] text-slate-500">Invoice Date</span>
                    <span className="text-[13px] font-medium text-slate-700">{formatDate(invoice.invoice_date)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[12px] text-slate-500">Due Date</span>
                    <span className="text-[13px] font-medium text-slate-700">{formatDate(invoice.due_date)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[12px] text-slate-500">Payment Terms</span>
                    <span className="text-[13px] font-medium text-slate-700">{invoice.payment_terms?.replace('_', ' ') || 'Net 30'}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[12px] text-slate-500">Purchase Type</span>
                    <span className="text-[13px] font-medium text-slate-700 capitalize">{invoice.purchase_type || 'Goods'}</span>
                  </div>
                </div>
              </div>

              {/* Payment Status Card */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-sm">
                    <DollarSign className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-[14px] font-semibold text-slate-800">Payment Status</h2>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-[12px] text-slate-500">Amount Paid</span>
                    <span className="text-[13px] font-medium text-emerald-600">₹{(invoice.amount_paid || 0).toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[12px] text-slate-500">Balance Due</span>
                    <span className={`text-[13px] font-medium ${(invoice.balance_due || 0) > 0 ? 'text-amber-600' : 'text-slate-400'}`}>
                      ₹{(invoice.balance_due || 0).toLocaleString('en-IN')}
                    </span>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="mt-3">
                    <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-teal-500 rounded-full transition-all" 
                        style={{ width: `${Math.min(100, ((invoice.amount_paid || 0) / (invoice.grand_total || 1)) * 100)}%` }}
                      />
                    </div>
                    <p className="text-[11px] text-slate-500 mt-1 text-center">
                      {Math.round(((invoice.amount_paid || 0) / (invoice.grand_total || 1)) * 100)}% paid
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* CENTER COLUMN - Items Table (6 cols) */}
            <div className="col-span-12 lg:col-span-6">
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden hover:shadow-md transition-shadow duration-200">
                <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-sm">
                      <Package className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h2 className="text-[14px] font-semibold text-slate-800">Invoice Items</h2>
                      <p className="text-[12px] text-slate-500">{items.length} item{items.length !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                </div>
                
                {items.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide">#</th>
                          <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Item Details</th>
                          <th className="px-5 py-3 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Qty</th>
                          <th className="px-5 py-3 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Rate</th>
                          <th className="px-5 py-3 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Tax</th>
                          <th className="px-5 py-3 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {items.map((item, index) => (
                          <tr key={index} className="hover:bg-slate-50/50 transition-colors duration-150">
                            <td className="px-5 py-4 text-[13px] text-slate-400 font-medium">{index + 1}</td>
                            <td className="px-5 py-4">
                              <p className="text-[14px] font-medium text-slate-800">{item.item_name}</p>
                              {item.hsn_sac && (
                                <p className="text-[11px] text-slate-400 mt-0.5">HSN: {item.hsn_sac}</p>
                              )}
                            </td>
                            <td className="px-5 py-4 text-center">
                              <span className="inline-flex items-center px-2.5 py-1 bg-slate-100 rounded-lg text-[13px] font-medium text-slate-700">
                                {item.quantity} {item.unit || 'Nos'}
                              </span>
                            </td>
                            <td className="px-5 py-4 text-right text-[13px] text-slate-600">
                              ₹{(item.purchase_rate || item.rate || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </td>
                            <td className="px-5 py-4 text-right text-[13px] text-slate-600">
                              {item.tax_percent || 0}%
                            </td>
                            <td className="px-5 py-4 text-right">
                              <span className="text-[14px] font-semibold text-emerald-600">
                                ₹{(item.line_total || item.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="px-5 py-12 text-center">
                    <Package className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p className="text-[14px] text-slate-500">No items in this invoice</p>
                  </div>
                )}
              </div>

              {/* Payment History Card */}
              {invoice.payment_history?.length > 0 && (
                <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden hover:shadow-md transition-shadow duration-200 mt-5">
                  <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-sm">
                        <DollarSign className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <h2 className="text-[14px] font-semibold text-slate-800">Payment History</h2>
                        <p className="text-[12px] text-slate-500">{invoice.payment_history.length} payment{invoice.payment_history.length !== 1 ? 's' : ''}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Date</th>
                          <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Payment #</th>
                          <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Mode</th>
                          <th className="px-5 py-3 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {invoice.payment_history.map((payment, idx) => (
                          <tr 
                            key={idx} 
                            className="hover:bg-teal-50/30 cursor-pointer transition-colors"
                            onClick={() => navigate(`/business/${bid}/payments-made/${payment.id}`)}
                          >
                            <td className="px-5 py-4 text-[13px] text-slate-600">
                              {formatDate(payment.payment_date)}
                            </td>
                            <td className="px-5 py-4">
                              <span className="text-[13px] font-medium text-teal-600">{payment.payment_no}</span>
                            </td>
                            <td className="px-5 py-4">
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-600">
                                {payment.payment_mode?.toUpperCase() || 'CASH'}
                              </span>
                            </td>
                            <td className="px-5 py-4 text-right">
                              <span className="text-[14px] font-semibold text-emerald-600">
                                ₹{(payment.allocations?.find(a => a.invoice_id === invoice.id)?.allocated_amount || payment.amount_paid || 0).toLocaleString('en-IN')}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {/* RIGHT COLUMN - Summary (3 cols) */}
            <div className="col-span-12 lg:col-span-3 space-y-4">
              
              {/* Amount Summary Card */}
              <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl p-5 shadow-sm border border-teal-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center shadow-sm">
                    <DollarSign className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-[14px] font-semibold text-slate-800">Amount Summary</h2>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-[12px] text-slate-600">Subtotal</span>
                    <span className="text-[13px] font-medium text-slate-700">₹{(invoice.subtotal || 0).toLocaleString('en-IN')}</span>
                  </div>
                  
                  {(invoice.discount_amount || 0) > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-[12px] text-slate-600">Discount</span>
                      <span className="text-[13px] font-medium text-red-600">-₹{invoice.discount_amount.toLocaleString('en-IN')}</span>
                    </div>
                  )}
                  
                  <div className="flex justify-between items-center">
                    <span className="text-[12px] text-slate-600">Tax Total</span>
                    <span className="text-[13px] font-medium text-slate-700">₹{(invoice.tax_total || 0).toLocaleString('en-IN')}</span>
                  </div>
                  
                  <div className="border-t border-teal-200 pt-3 flex justify-between items-center">
                    <span className="text-[14px] font-semibold text-slate-700">Grand Total</span>
                    <span className="text-[20px] font-bold text-teal-600">
                      ₹{(invoice.grand_total || 0).toLocaleString('en-IN')}
                    </span>
                  </div>
                </div>
              </div>

              {/* Quick Actions Card */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
                <h3 className="text-[14px] font-semibold text-slate-800 mb-4">Quick Actions</h3>
                <div className="space-y-2">
                  {['unpaid', 'partially_paid', 'overdue'].includes(invoice.status) && (
                    <button
                      onClick={() => navigate(`/business/${bid}/payments-made/new?supplier_id=${invoice.supplier_id}&invoice_id=${invoice.id}`)}
                      className="w-full px-4 py-2.5 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-all duration-200 text-[13px] font-medium flex items-center gap-2 justify-center"
                    >
                      <DollarSign className="w-4 h-4" />
                      Record Payment
                    </button>
                  )}
                  <button onClick={async () => { try { const r = await purchaseInvoicesAPI.pdf(id); const u = window.URL.createObjectURL(r.data); const a = document.createElement('a'); a.href = u; a.download = `PI_${invoice?.invoice_number}.pdf`; a.click(); window.URL.revokeObjectURL(u); } catch(e) { toast.error(getApiErrorMessage(e, 'PDF download failed')); }}} className="w-full px-4 py-2.5 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition-all duration-200 text-[13px] font-medium flex items-center gap-2 justify-center">
                    <Download className="w-4 h-4" />
                    Download PDF
                  </button>
                </div>
              </div>

              {/* Notes Card */}
              {invoice.notes && (
                <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
                  <h3 className="text-[14px] font-semibold text-slate-800 mb-3">Notes</h3>
                  <p className="text-[13px] text-slate-600 leading-relaxed">{invoice.notes}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchaseInvoiceDetailPage;
